import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Linking, Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react-native';
import { useResponsive } from '../../hooks/useResponsive';
import { useBillingSummary } from '../../hooks/useBillingSummary';
import { usePendingBillingIntent } from '../../hooks/usePendingBillingIntent';
import { apiService } from '../../services/apiService';
import { writeBillingSnapshot } from '../../utils/billingSnapshot';
import { trackBillingAction } from '../../utils/billingAnalytics';
import BillingModalHeader from './BillingModalHeader';
import BillingModalPlanSummary from './BillingModalPlanSummary';
import BillingModalPaymentMethod from './BillingModalPaymentMethod';
import BillingModalPrimaryAction from './BillingModalPrimaryAction';
import BillingModalCancelConfirm from './BillingModalCancelConfirm';
import BillingModalSwitchConfirm from './BillingModalSwitchConfirm';
import BillingModalRecentCharges from './BillingModalRecentCharges';
import BillingModalFooter from './BillingModalFooter';
import BillingModalStateConflictBanner from './BillingModalStateConflictBanner';

const SUB_STATES = {
  idle: 'idle',
  cancel_confirm: 'cancel_confirm',
  switch_preview: 'switch_preview',
  post_success: 'post_success',
  error: 'error',
  portal_return_flash: 'portal_return_flash',
};

function reducer(state, action) {
  switch (action.type) {
    case 'OPEN_CANCEL_CONFIRM': return { ...state, subState: SUB_STATES.cancel_confirm };
    case 'OPEN_SWITCH_PREVIEW': return { ...state, subState: SUB_STATES.switch_preview };
    case 'COLLAPSE': return { ...state, subState: SUB_STATES.idle };
    case 'SHOW_SUCCESS': return { ...state, subState: SUB_STATES.post_success, lastResult: action.payload };
    case 'SHOW_ERROR': return { ...state, subState: SUB_STATES.error, lastError: action.payload };
    case 'SHOW_CONFLICT': return { ...state, stateConflict: action.payload };
    case 'CLEAR_CONFLICT': return { ...state, stateConflict: null };
    case 'RESET': return { ...state, subState: SUB_STATES.idle, lastResult: null, lastError: null };
    default: return state;
  }
}

export default function BillingModal({ isOpen, onClose, source = 'settings', initialAction = null, userId }) {
  const router = useRouter();
  const posthog = usePostHog();
  const { isMobile } = useResponsive();
  const { data: summary, isLoading, error: fetchError, refresh } = useBillingSummary({ userId, enabled: isOpen });
  const { get: getPendingIntent, set: setPendingIntent, clear: clearPendingIntent } = usePendingBillingIntent({ userId });

  const [state, dispatch] = useReducer(reducer, {
    subState: SUB_STATES.idle,
    lastResult: null,
    lastError: null,
    stateConflict: null,
  });

  const openedAtRef = useRef(null);
  const lastActionRef = useRef(null);
  const analyticsFiredRef = useRef(false);

  // Analytics: open / dismiss (tasks 7.16a)
  useEffect(() => {
    if (!isOpen) return;
    openedAtRef.current = Date.now();
    if (!analyticsFiredRef.current && summary) {
      analyticsFiredRef.current = true;
      trackBillingAction(posthog, 'billing_modal_opened', {
        source,
        action: initialAction,
        status: summary.status ?? null,
        tier: summary.tier ?? null,
        interval: summary.plan?.interval ?? null,
      });
    }
  }, [isOpen, summary, source, initialAction, posthog]);

  // Ineligibility event (task 7.16c)
  useEffect(() => {
    if (!isOpen || !summary) return;
    if (summary.eligible === false) {
      trackBillingAction(posthog, 'billing_modal_ineligible', {
        reason: summary.reason,
        source,
      });
    }
    if (summary.state === 'malformed') {
      trackBillingAction(posthog, 'billing_modal_paddle_malformed', { source, status: summary.status });
    }
  }, [isOpen, summary, source, posthog]);

  // Auto-open sub-state for cancel/switch_plan (R2 AC 7). The update_card
  // deeplink auto-fire lives in a separate effect AFTER invokeBillingAction
  // is declared, to avoid a TDZ on the callback ref.
  useEffect(() => {
    if (!isOpen || !summary || state.subState !== SUB_STATES.idle) return;
    if (!initialAction) return;
    switch (initialAction) {
      case 'cancel': dispatch({ type: 'OPEN_CANCEL_CONFIRM' }); break;
      case 'switch_plan': dispatch({ type: 'OPEN_SWITCH_PREVIEW' }); break;
      default: /* update_card auto-fires below; resume/reactivate need primary-button click */ break;
    }
  }, [isOpen, summary, initialAction, state.subState]);

  // update_card auto-fire ref; declared here so the effect below can guard
  // against React 18 double-invoke in StrictMode + the deeplink reopening.
  const updateCardAutoFiredRef = useRef(false);

  // Pending intent carryover: if a 409 happened before, show banner on fresh open
  useEffect(() => {
    if (!isOpen || !summary) return;
    const intent = getPendingIntent();
    if (intent) dispatch({ type: 'SHOW_CONFLICT', payload: intent });
  }, [isOpen, summary, getPendingIntent]);

  // ESC handler (web): collapse sub-state first, then close (R3 AC 10)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (state.subState !== SUB_STATES.idle) {
        dispatch({ type: 'COLLAPSE' });
      } else {
        handleClose();
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [isOpen, state.subState]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(() => {
    const duration = openedAtRef.current ? Date.now() - openedAtRef.current : 0;
    trackBillingAction(posthog, 'billing_modal_dismissed', {
      source,
      duration_ms: duration,
      status: summary?.status ?? null,
      reached_sub_state: state.subState,
      last_action_clicked: lastActionRef.current,
    });
    analyticsFiredRef.current = false;
    openedAtRef.current = null;
    dispatch({ type: 'RESET' });
    onClose?.();
  }, [summary, source, state.subState, posthog, onClose]);

  const handleBackdrop = useCallback(() => {
    if (state.subState !== SUB_STATES.idle) {
      dispatch({ type: 'COLLAPSE' });
    } else {
      handleClose();
    }
  }, [state.subState, handleClose]);

  const [inlineActionError, setInlineActionError] = useState(null);

  // Directly invokes billing-action for primary-button flows that don't need a
  // sub-state confirmation panel (resume, reactivate, update_card). Reloads
  // the summary on success so the modal reflects the new state in-place.
  const invokeBillingAction = useCallback(async (action, opts = {}) => {
    setInlineActionError(null);
    const started = Date.now();
    trackBillingAction(posthog, 'billing_action_clicked', { action, status: summary?.status, source });

    try {
      if (opts.openUrl) writeBillingSnapshot(summary);
      const idempotencyKey = crypto.randomUUID();
      const body = await apiService.invokeEdgeFunction('billing-action', {
        body: { action },
        headers: { 'Idempotency-Key': idempotencyKey },
      });

      trackBillingAction(posthog, 'billing_action_completed', {
        action, status: summary?.status, source, duration_ms: Date.now() - started,
      });

      if (opts.openUrl && body?.portalUrl) {
        try { window.open(body.portalUrl, '_blank', 'noopener,noreferrer'); }
        catch { Linking.openURL(body.portalUrl); }
        return;
      }

      dispatch({ type: 'SHOW_SUCCESS', payload: body });
      await refresh({ force: true });
    } catch (e) {
      trackBillingAction(posthog, 'billing_action_failed', {
        action, status: summary?.status, source, duration_ms: Date.now() - started, error_code: e?.code ?? e?.message,
      });
      Sentry.captureException(e, {
        tags: {
          component: 'BillingModal', action,
          status: String(e?.status ?? 'n/a'), error_code: e?.code ?? 'n/a',
        },
        contexts: { billing: { source, summary_status: summary?.status } },
      });
      if (e?.status === 409 && e?.code === 'state_conflict') {
        setPendingIntent({ action, args: {} });
        await refresh({ force: true });
        dispatch({ type: 'SHOW_CONFLICT', payload: { action, args: {} } });
        return;
      }
      setInlineActionError({
        action,
        code: e?.code ?? null,
        status: e?.status ?? null,
        body: e?.body ?? null,
        message: e?.message ?? null,
      });
    }
  }, [posthog, source, summary, refresh, setPendingIntent]);

  // J04/J05 past-due dunning emails link with ?action=update_card. The user
  // already clicked "Update card" in the email, so the modal opens AND
  // immediately invokes the Paddle portal open — no redundant second click.
  // Guarded by a ref so React 18 StrictMode double-mount and re-renders
  // don't fire twice. resume/reactivate are deliberately NOT auto-fired:
  // a stray deeplink shouldn't mutate subscription state without intent.
  useEffect(() => {
    if (!isOpen || !summary || state.subState !== SUB_STATES.idle) return;
    if (initialAction !== 'update_card') return;
    if (updateCardAutoFiredRef.current) return;
    updateCardAutoFiredRef.current = true;
    invokeBillingAction('update_card', { openUrl: true });
  }, [isOpen, summary, initialAction, state.subState, invokeBillingAction]);

  // Reset the auto-fire latch when the modal closes, so re-opening the modal
  // (e.g. user closed Paddle portal and re-clicks the email) re-fires.
  useEffect(() => {
    if (!isOpen) updateCardAutoFiredRef.current = false;
  }, [isOpen]);

  // Wire primary action dispatch to sub-state transitions or direct calls
  const handlePrimaryAction = useCallback(async (action) => {
    lastActionRef.current = action;
    switch (action) {
      case 'switch_preview': dispatch({ type: 'OPEN_SWITCH_PREVIEW' }); break;
      case 'cancel_confirm':
        // Route to the existing retention flow at /cancel-subscription instead
        // of the in-modal confirm panel. Keeps a single cancellation UX across
        // the product and preserves retention offers already wired there.
        onClose?.();
        router.push('/cancel-subscription');
        break;
      case 'go_to_paywall':
        onClose?.();
        router.push('/pricing');
        break;
      case 'compare_plans':
        // Step 8: carded users switch plan / get the Pass from /pricing (the
        // switch hub), not in-modal. Close and route there.
        onClose?.();
        router.push('/pricing');
        break;
      case 'resume':
      case 'reactivate':
        await invokeBillingAction(action);
        break;
      case 'update_card':
        await invokeBillingAction('update_card', { openUrl: true });
        break;
      default: break;
    }
  }, [onClose, router, invokeBillingAction]);

  // Step 8: the carded->CBP "Get the Pass" affordance moved to /pricing (the
  // switch hub). The BillingModal carded menu is now Compare Plans + Cancel.

  const handleSuccess = useCallback(async (result) => {
    clearPendingIntent();
    dispatch({ type: 'SHOW_SUCCESS', payload: result });
    await refresh({ force: true });
  }, [refresh, clearPendingIntent]);

  const handleStateConflict = useCallback(async (intent) => {
    setPendingIntent(intent);
    await refresh({ force: true });
    dispatch({ type: 'SHOW_CONFLICT', payload: intent });
    dispatch({ type: 'COLLAPSE' });
  }, [refresh, setPendingIntent]);

  const handleConflictRetry = useCallback(() => {
    const intent = state.stateConflict;
    dispatch({ type: 'CLEAR_CONFLICT' });
    if (!intent) return;
    if (intent.action === 'cancel') dispatch({ type: 'OPEN_CANCEL_CONFIRM' });
    else if (intent.action === 'switch_plan') dispatch({ type: 'OPEN_SWITCH_PREVIEW' });
  }, [state.stateConflict]);

  const handleConflictDismiss = useCallback(() => {
    clearPendingIntent();
    dispatch({ type: 'CLEAR_CONFLICT' });
  }, [clearPendingIntent]);

  const handleRouteRetention = useCallback(() => {
    onClose?.();
    router.push('/cancel-subscription');
  }, [onClose, router]);

  return (
    <Modal visible={isOpen} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={handleBackdrop}>
      <View style={styles.backdrop}>
        <Pressable style={StyleSheet.absoluteFill} accessibilityLabel="Close billing modal" onPress={handleBackdrop} />
        <View style={[styles.sheet, isMobile ? styles.sheetMobile : styles.sheetDesktop]}>
          <BillingModalHeader subState={state.subState} status={summary?.status} onClose={handleClose} />

          <ScrollView contentContainerStyle={{ paddingBottom: 20 }}>
            {state.stateConflict && (
              <BillingModalStateConflictBanner
                pendingIntent={state.stateConflict}
                currentStatus={summary?.status}
                onRetry={handleConflictRetry}
                onDismiss={handleConflictDismiss}
              />
            )}

            {fetchError && !summary ? (
              <FetchErrorPanel error={fetchError} onRetry={() => refresh({ force: true })} />
            ) : summary?.eligible === false ? (
              <IneligiblePanel reason={summary.reason} onClose={handleClose} onGoToPaywall={() => { onClose?.(); router.push('/pricing'); }} />
            ) : (
              <>
                <BillingModalPlanSummary summary={summary} isLoading={isLoading} />

                {summary?.fallback && state.subState === SUB_STATES.idle && (
                  <FallbackPanel fallback={summary.fallback} onRefresh={() => refresh({ force: true })} />
                )}

                {summary?.status === 'grandfather_active' && !summary?.fallback && state.subState === SUB_STATES.idle && (
                  <GrandfatherNotice />
                )}

                {!summary?.fallback && state.subState === SUB_STATES.idle && (
                  <>
                    <BillingModalPrimaryAction summary={summary} onAction={handlePrimaryAction} />
                    {inlineActionError && (
                      <Text role="alert" style={styles.inlineError}>
                        {formatInlineActionError(inlineActionError)}
                      </Text>
                    )}
                    <BillingModalPaymentMethod summary={summary} source={source} />
                    {/* Hide the billing-history / "See all billing history" (full_portal)
                        entry for users with no Paddle customer (grandfather_active,
                        trial_active) — full_portal 422s with no_paddle_customer for them.
                        Defaults to shown when the flag is absent (older summary payloads)
                        so paid users keep their history during rollout. */}
                    {summary?.hasPaddleCustomer !== false && (
                      <BillingModalRecentCharges summary={summary} source={source} />
                    )}
                  </>
                )}

                {state.subState === SUB_STATES.cancel_confirm && summary && (
                  <BillingModalCancelConfirm
                    summary={summary}
                    source={source}
                    onSuccess={handleSuccess}
                    onBack={() => dispatch({ type: 'COLLAPSE' })}
                    onStateConflict={handleStateConflict}
                    onRouteRetention={handleRouteRetention}
                  />
                )}

                {state.subState === SUB_STATES.switch_preview && summary && (
                  <BillingModalSwitchConfirm
                    summary={summary}
                    source={source}
                    onSuccess={handleSuccess}
                    onBack={() => dispatch({ type: 'COLLAPSE' })}
                    onStateConflict={handleStateConflict}
                  />
                )}

                {state.subState === SUB_STATES.post_success && (
                  <SuccessPanel result={state.lastResult} summary={summary} onDone={() => dispatch({ type: 'RESET' })} />
                )}
              </>
            )}

            <BillingModalFooter summary={summary} />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function IneligiblePanel({ reason, onClose, onGoToPaywall }) {
  const copy = reason === 'admin_or_internal'
    ? { title: 'No subscription on this account', body: 'Admin and internal accounts do not have a Pro subscription to manage.' }
    : { title: 'No Pro subscription yet', body: 'Upgrade to Pro to unlock full access and manage your billing from here.' };
  return (
    <View style={panelStyles.root}>
      <Text style={panelStyles.title}>{copy.title}</Text>
      <Text style={panelStyles.body}>{copy.body}</Text>
      {reason !== 'admin_or_internal' && (
        <Pressable onPress={onGoToPaywall} style={styles.primaryBtn}>
          <Text style={panelStyles.primaryBtnText}>Upgrade to Pro</Text>
        </Pressable>
      )}
    </View>
  );
}

function FetchErrorPanel({ error, onRetry }) {
  const code = error?.code || error?.message || 'fetch_failed';
  const status = error?.status;
  return (
    <View style={panelStyles.root}>
      <Text style={panelStyles.title}>We could not load your billing details</Text>
      <Text style={panelStyles.body}>
        Please try again. If this keeps happening, email support@doocars.com with the reference below.
      </Text>
      <Text style={panelStyles.diag}>
        {status ? `status ${status}` : ''}{status && code ? ' · ' : ''}{code}
      </Text>
      <Pressable onPress={onRetry} style={styles.primaryBtn}>
        <Text style={panelStyles.primaryBtnText}>Try again</Text>
      </Pressable>
    </View>
  );
}

function formatInlineActionError({ action, code, status, body, message }) {
  const resolved = code || body?.error || null;
  switch (resolved) {
    case 'grace_window_expired':
      return 'Your grace window has already ended. Visit pricing to resubscribe.';
    case 'no_active_subscription':
      return 'No active subscription on this account. Contact support@doocars.com.';
    case 'no_paddle_customer':
      return 'We could not locate your Paddle record. Email support@doocars.com with the reference in the footer.';
    case 'portal_session_failed':
      return 'The billing portal is temporarily unavailable. Please try again in a moment.';
    case 'paddle_error': {
      const detail = body?.paddle_detail;
      return detail
        ? `Our billing provider returned an error: ${detail}.`
        : 'Our billing provider returned an error. Please try again.';
    }
    case 'paddle_unavailable':
      return 'Our billing provider is not responding right now. Please try again in a moment.';
    case 'rate_limited':
      return 'Too many requests. Please wait a minute and try again.';
    case 'internal_error':
      return 'Something went wrong on our end. Please try again, or email support@doocars.com.';
    default:
      // Action-aware fallback when the code is unrecognised.
      switch (action) {
        case 'resume': return 'We could not resume your subscription. Please try again.';
        case 'reactivate': return 'We could not reactivate your subscription. Please try again.';
        case 'update_card': return 'We could not open the payment method update. Please try again.';
        default: return message || 'Could not complete that action. Please try again.';
      }
  }
}

function FallbackPanel({ fallback, onRefresh }) {
  const toneStyle = fallback?.tone === 'error' ? panelStyles.fallbackError : panelStyles.fallbackWarning;
  const handlePrimary = () => {
    switch (fallback?.primary?.action) {
      case 'email_support':
        Linking.openURL('mailto:support@doocars.com?subject=Billing%20support').catch(() => {});
        break;
      case 'refresh':
        onRefresh?.();
        break;
      default: break;
    }
  };
  const handleSecondary = () => {
    if (!fallback?.secondary) return;
    switch (fallback.secondary.action) {
      case 'email_support':
        Linking.openURL('mailto:support@doocars.com?subject=Billing%20support').catch(() => {});
        break;
      case 'refresh':
        onRefresh?.();
        break;
      default: break;
    }
  };
  return (
    <View style={[panelStyles.fallbackBase, toneStyle]}>
      <Text style={panelStyles.title}>{fallback?.title}</Text>
      <Text style={panelStyles.body}>{fallback?.body}</Text>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {fallback?.secondary && (
          <Pressable onPress={handleSecondary} style={({ pressed }) => [panelStyles.fallbackBtnSecondary, pressed && { opacity: 0.7 }]}>
            <Text style={panelStyles.fallbackBtnSecondaryText}>{fallback.secondary.label}</Text>
          </Pressable>
        )}
        {fallback?.primary && fallback.primary.action !== 'none' && (
          <Pressable onPress={handlePrimary} style={({ pressed }) => [panelStyles.fallbackBtnPrimary, pressed && { opacity: 0.8 }]}>
            <Text style={panelStyles.fallbackBtnPrimaryText}>{fallback.primary.label}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

function GrandfatherNotice() {
  return (
    <View style={panelStyles.notice}>
      <Text style={panelStyles.noticeText}>
        Your access is granted as part of the Founder's Rate. There is no billing to manage. If you need help, email support@doocars.com.
      </Text>
    </View>
  );
}

function SuccessPanel({ result, summary, onDone }) {
  return (
    <View style={panelStyles.root}>
      <Text style={panelStyles.title}>All set</Text>
      <Text style={panelStyles.body}>
        {result?.writtenToSupabase === false
          ? 'Your change went through with Paddle. It may take a moment to update here.'
          : 'Your subscription has been updated.'}
      </Text>
      <Pressable onPress={onDone} style={styles.primaryBtn}>
        <Text style={panelStyles.primaryBtnText}>Done</Text>
      </Pressable>
    </View>
  );
}

const panelStyles = StyleSheet.create({
  root: { paddingHorizontal: 20, paddingVertical: 18, gap: 12 },
  title: { fontSize: 17, fontWeight: '600', color: '#FFFFFF' },
  body: { fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 20 },
  diag: { fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' },
  primaryBtnText: { color: '#FFFFFF', fontSize: 15, fontWeight: '600' },
  notice: { marginHorizontal: 20, marginVertical: 10, padding: 12, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)' },
  noticeText: { fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 18 },
  fallbackBase: { marginHorizontal: 20, marginVertical: 10, padding: 14, borderRadius: 10, borderWidth: 1, gap: 10 },
  fallbackWarning: { backgroundColor: 'rgba(255,123,0,0.08)', borderColor: 'rgba(255,123,0,0.4)' },
  fallbackError: { backgroundColor: 'rgba(255,0,0,0.08)', borderColor: 'rgba(255,0,0,0.4)' },
  fallbackBtnPrimary: { flex: 1, backgroundColor: '#0556F7', paddingVertical: 10, borderRadius: 8, alignItems: 'center' },
  fallbackBtnPrimaryText: { color: '#FFFFFF', fontSize: 13, fontWeight: '600' },
  fallbackBtnSecondary: { flex: 1, backgroundColor: 'transparent', paddingVertical: 10, borderRadius: 8, alignItems: 'center', borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)' },
  fallbackBtnSecondaryText: { color: '#FFFFFF', fontSize: 13, fontWeight: '600' },
});

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.7)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: '#1D242F', maxHeight: '90%', borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)' },
  sheetMobile: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
  sheetDesktop: { margin: 'auto', borderRadius: 16, width: 480, maxWidth: '95%' },
  primaryBtn: { paddingVertical: 12, borderRadius: 10, alignItems: 'center', backgroundColor: '#0556F7' },
  inlineError: { marginHorizontal: 20, marginTop: 4, marginBottom: 8, fontSize: 13, color: '#FF6B6B' },
});
